import { useEffect, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { FullScreenLoading, InlineCtaCard, TIER_TO_TRACK_CODE } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { useSurveyData } from '@backrs_ui/grow-ui/src/hooks/useSurveyData';
import { hydrateEducationalInvestment } from '@backrs_ui/grow-ui/src/pages/EducationalInvestment/utils';
import { openSignupPrompt } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { useGetEducationalInvestmentsByCareerTrackQuery } from '@backrs_ui/grow-ui/src/redux/educationalInvestment/educationalInvestment.api';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectSharedBlueprintData } from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';
import { useDemoAwareRoute } from '@backrs_ui/grow-ui/src/utils/demoUtils';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { BlueprintOutputPage } from './BlueprintOutputPage';

type MyResultsPageContainerProps = Readonly<{
  isShared?: boolean;
}>;

export const MyResultsPageContainer = ({ isShared = false }: MyResultsPageContainerProps) => {
  const sharedBlueprintData = useAppSelector(selectSharedBlueprintData);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const surveyRoute = useDemoAwareRoute(APP_ROUTE.SURVEY);
  const { loggedIn } = useAuth();

  const { mostRecentUserResponseSet, surveyResults, user } = useSurveyData();
  const loadingBlueprint = useAppSelector(
    (state) => state.userResponseSet.loadingMostRecentUserResponseSet,
  );
  const hasBlueprint = !!surveyResults && !!mostRecentUserResponseSet;

  // Guest visitors must create an account before they can view their blueprint.
  // It is already persisted against their guest session and claimed on signup.
  const mustSignUp = !isShared && !loggedIn;

  useEffect(() => {
    if (mustSignUp && hasBlueprint) {
      dispatch(openSignupPrompt('blueprint'));
    }
  }, [mustSignUp, hasBlueprint, dispatch]);

  // Fetch educational investments for the user's tier
  const tierToUse =
    isShared && sharedBlueprintData ? sharedBlueprintData.tier : surveyResults?.tier;
  const { data: rawEducationalInvestment, isLoading: isLoadingEducationalInvestment } =
    useGetEducationalInvestmentsByCareerTrackQuery({
      careerTrackCode: tierToUse ? TIER_TO_TRACK_CODE[tierToUse] : undefined,
    });

  const educationalInvestment = useMemo(
    () =>
      rawEducationalInvestment
        ? {
            target: rawEducationalInvestment.target.map(hydrateEducationalInvestment),
            others: rawEducationalInvestment.others.map(hydrateEducationalInvestment),
          }
        : undefined,
    [rawEducationalInvestment],
  );

  if (isShared && sharedBlueprintData) {
    return (
      <Box sx={outputContainerSx}>
        <BlueprintOutputPage
          tier={sharedBlueprintData.tier}
          careerPathSuggestions={sharedBlueprintData.careerPathSuggestions}
          userResponseSet={sharedBlueprintData.userResponseSet}
          educationalInvestment={educationalInvestment}
          isShared={true}
          isReadOnly={true}
        />
      </Box>
    );
  }

  // shared viewer past the SharedSessionProvider gate but no payload — show error
  // instead of falling through and rendering the logged-in viewer's own blueprint
  if (isShared) {
    return (
      <Box sx={sharedErrorContainerSx}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {appStrings.modules.sharedSession.blueprintLoadError}
        </Typography>
      </Box>
    );
  }

  if (loadingBlueprint) {
    return <FullScreenLoading />;
  }

  // no blueprint to show — send them to the quiz
  if (!surveyResults || !mostRecentUserResponseSet) {
    return <Navigate to={surveyRoute} replace />;
  }

  if (mustSignUp) {
    return (
      <Box sx={gatedPreviewContainerSx}>
        <InlineCtaCard
          title={appStrings.modules.blueprintOutput.signUpToView.title}
          supportingText={appStrings.modules.blueprintOutput.signUpToView.subtitle}
          primaryAction={{
            label: appStrings.modules.blueprintOutput.signUpToView.cta,
            onClick: () => dispatch(openSignupPrompt('blueprint')),
            id: 'blueprint-gate-signup-btn',
          }}
          secondaryAction={{
            label: appStrings.modules.blueprintOutput.signUpToView.goBack,
            onClick: () => navigate(surveyRoute, { state: { startAtLastQuestion: true } }),
            id: 'blueprint-gate-go-back-btn',
          }}
        />
        <Box sx={blurredPreviewWrapperSx}>
          <Box sx={blurredBlueprintSx} aria-hidden>
            <BlueprintOutputPage
              tier={surveyResults.tier}
              careerPathSuggestions={surveyResults.careerPathSuggestions}
              userResponseSet={mostRecentUserResponseSet}
              educationalInvestment={educationalInvestment}
              isReadOnly={true}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  if (isLoadingEducationalInvestment) {
    return <FullScreenLoading />;
  }

  return (
    <Box sx={outputContainerSx}>
      <BlueprintOutputPage
        tier={surveyResults.tier}
        careerPathSuggestions={surveyResults.careerPathSuggestions}
        userResponseSet={mostRecentUserResponseSet}
        educationalInvestment={educationalInvestment}
        isReadOnly={!user?.access_token}
      />
    </Box>
  );
};

const outputContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
};

const sharedErrorContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
  padding: 4,
};

const gatedPreviewContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  alignSelf: 'stretch',
  minHeight: 0,
  gap: backrs.spacing['spacing-2xl'],
});

const blurredPreviewWrapperSx: SxProps<Theme> = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  width: '100%',
};

const blurredBlueprintSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: backrs.spacing['spacing-md'],
  overflow: 'clip',
  filter: 'blur(6px)',
  pointerEvents: 'none',
  userSelect: 'none',
});
